import React from 'react';
import { motion } from 'framer-motion';
import { Tv, Zap, Check, Star, Wifi, Headphones, Film } from 'lucide-react';

const TariffPlans = () => {
    const plans = [
        {
            name: 'Econom + IPTV',
            price: '5900 դրամ',
            download: '25 ՄԲԻԹ/ՎՐԿ',
            upload: '114 ՄԲԻԹ/ՎՐԿ',
            features: [
                'Անվճար WIFI ակտիվացում',
                '100+ Հեռուստաալիք',
                '24/7 Տեխնիկական աջակցություն'
            ],
            popular: false,
            icon: <Tv size={28} strokeWidth={1.5} />,
            accentColor: '#EF4444' // Red-500
        },
        {
            name: 'Standart + IPTV',
            price: '6900 դրամ',
            download: '40 ՄԲԻԹ/ՎՐԿ',
            upload: '114 ՄԲԻԹ/ՎՐԿ',
            features: [
                'Անվճար WIFI ակտիվացում',
                '150+ Հեռուստաալիք',
                'HD որակի ալիքներ',
                '24/7 Տեխնիկական աջակցություն'
            ],
            popular: true,
            icon: <Film size={28} strokeWidth={1.5} />,
            accentColor: '#DC2626' // Red-600
        },
        {
            name: 'Ultra + IPTV',
            price: '8900 դրամ',
            download: '60 ՄԲԻԹ/ՎՐԿ',
            upload: '114 ՄԲԻԹ/ՎՐԿ',
            features: [
                'Անվճար WIFI ակտիվացում',
                '200+ Հեռուստաալիք',
                '4K Ultra HD ալիքներ',
                'ՎԻՓ հեռուստաալիքներ',
                '24/7 Պրեմիում աջակցություն'
            ],
            popular: false,
            icon: <Star size={28} strokeWidth={1.5} />,
            accentColor: '#B91C1C' // Red-700
        }
    ];

    const FeatureIcon = ({ index }) => {
        const icons = [<Wifi key="wifi" />, <Headphones key="support" />, <Film key="tv" />];
        return icons[index % icons.length];
    };

    return (

        <section  className="relative py-12 bg-gradient-to-b from-white to-red-50 overflow-hidden" id="tariffs">
            <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0 bg-red-500 rounded-full filter blur-3xl opacity-20 mix-blend-multiply"></div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                {/* Animated Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl md:text-5xl font-extrabold mb-4">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-red-500 to-red-700">
              ԸՆՏՐԵՔ ՁԵՐ ՏԱՐԻՖԱՅԸ
            </span>
                        <br />
                        <span className="relative inline-block">
              <span className="relative z-10">ՄԵԿ ՏՈՒՓՈՎ</span>
              <span className="absolute bottom-0 left-0 w-full h-2 bg-red-500 opacity-20 rounded-full"></span>
            </span>
                    </h2>
                    <p className="text-lg text-red-900/70 max-w-2xl mx-auto leading-relaxed">
                        Բարձր արագությամբ ինտերնետ և հեռուստատեսություն մեկ տուփով
                    </p>
                </motion.div>

                {/* Plans Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {plans.map((plan, idx) => (
                        <motion.div
                            key={plan.name}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.5, delay: idx * 0.1 }}
                            whileHover={{ y: -10 }}
                            className={`relative flex flex-col h-full rounded-2xl overflow-hidden shadow-lg 
                ${plan.popular ? 'ring-2 ring-red-500 shadow-xl transform scale-[1.02]' : 'border border-gray-100'}`}
                            style={{ '--accent-color': plan.accentColor }}
                        >
                            {plan.popular && (
                                <div className="absolute top-4 right-4 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 z-10">
                                    <Star size={16} className="fill-current" />
                                    <span>ԱՄԵՆԱՓՈՓՈԽԱԿԱՆ</span>
                                </div>
                            )}

                            {/* Card Header */}
                            <div className="relative bg-gradient-to-br from-[var(--accent-color)] to-red-800 text-white p-8 text-center overflow-hidden">
                                <div className="absolute inset-0 opacity-10">
                                    <div className="absolute inset-0 bg-white rounded-full filter blur-xl"></div>
                                </div>

                                <div className="relative z-10">
                                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center">
                                        {React.cloneElement(plan.icon, { className: "text-white" })}
                                    </div>
                                    <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                                    <div className="flex justify-center items-baseline gap-2">
                                        <span className="text-3xl font-extrabold">{plan.price}</span>
                                        <span className="text-white/80">/ամսական</span>
                                    </div>
                                </div>
                            </div>

                            {/* Speeds */}
                            <div className="p-6 bg-white/50 border-b border-gray-100">
                                <div className="space-y-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                                            <Zap className="text-red-600" size={16} />
                                        </div>
                                        <div className="flex flex-wrap items-baseline gap-1">
                                            <span className="text-gray-600">Download:</span>
                                            <span className="font-bold text-red-700">{plan.download}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                                            <Zap className="text-red-600" size={16} />
                                        </div>
                                        <div className="flex flex-wrap items-baseline gap-1">
                                            <span className="text-gray-600">Upload:</span>
                                            <span className="font-bold text-red-700">{plan.upload}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Features */}
                            <div className="p-6 flex-grow bg-white">
                                <h4 className="text-lg font-bold text-center text-gray-800 mb-4">ԾԱՌԱՅՈՒԹՅՈՒՆՆԵՐ</h4>
                                <ul className="space-y-3">
                                    {plan.features.map((feat, i) => (
                                        <motion.li
                                            key={i}
                                            className="flex items-start gap-2 pl-3 relative"
                                            whileHover={{ x: 5 }}
                                            transition={{ type: "spring", stiffness: 300 }}
                                        >
                                            <div className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-[var(--accent-color)]"></div>
                                            <Check className="flex-shrink-0 mt-0.5 text-red-600" size={18} />
                                            <span className="text-gray-700">{feat}</span>
                                            <div className="ml-auto opacity-10 text-[var(--accent-color)]">
                                                <FeatureIcon index={i} />
                                            </div>
                                        </motion.li>
                                    ))}
                                </ul>
                            </div>

                            {/* CTA Button */}

                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default TariffPlans;
